// EsignPinForm.tsx — общая презентационная форма ЭЦП (ИНН → код из SMS → подпись).
// Вынесена из DigitalSignDialog по решению D12 плана
// docs/plans/2026-07-31-agreement-versioning-v2.md: разметка, идентификаторы, тексты и условия
// disabled сохранены как были, поведение диалога подписи документов заявки не меняется.
// Компонент без собственного состояния и без сетевых вызовов — им владеет вызывающий диалог.
import styled from "styled-components";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MaskedTextField from "components/MaskedTextField";
import { useTranslation } from "react-i18next";
import CustomButton from "./Button";

interface EsignPinFormProps {
  title: string;
  pin: string;
  onPinChange: (value: string) => void;
  isLegal: boolean;
  onIsLegalChange: (value: boolean) => void;
  companyPin: string;
  onCompanyPinChange: (value: string) => void;
  /** true — код уже отправлен, показываем поле кода и кнопку «Подписать». */
  isSend: boolean;
  code: string;
  onCodeChange: (value: string) => void;
  onSendCode: () => void;
  onSign: () => void;
  onCancel: () => void;
  /** Текст кнопки отмены; по умолчанию — label:digitalSign.cancel. */
  cancelLabel?: string;
}

const EsignPinForm = (props: EsignPinFormProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Header>
        <MainText>{props.title}</MainText>
      </Header>

      <FormControlLabel
        control={
          <Checkbox
            checked={props.isLegal}
            onChange={(e) => {
              props.onIsLegalChange(e.target.checked);
              props.onCompanyPinChange("");
            }}
            disabled={props.isSend}
            id="id_dialog_sign_is_legal"
          />
        }
        label="Юридическое лицо"
        style={{ marginBottom: 8 }}
      />

      <MaskedTextField
        label={props.isLegal ? "ИНН человека" : t("label:digitalSign.inn")}
        value={props.pin}
        mask={"00000000000000"}
        onChange={(e) => props.onPinChange(e.target.value)}
        id="id_dialog_sign_pin"
        name="dialog_sign_pin"
      />

      {props.isLegal && (
        <MaskedTextField
          label="ИНН компании"
          value={props.companyPin}
          mask={"00000000000000"}
          onChange={(e) => props.onCompanyPinChange(e.target.value)}
          id="id_dialog_sign_company_pin"
          name="dialog_sign_company_pin"
          style={{ marginTop: 12 }}
        />
      )}

      {props.isSend && (
        <>
          <div style={{ margin: 15 }}>{t("label:digitalSign.pinCodeSentNotification")}</div>
          <MaskedTextField
            label={t("label:digitalSign.pinCode")}
            value={props.code}
            mask={"000000"}
            onChange={(e) => props.onCodeChange(e.target.value)}
            id="id_dialog_sign_pin_code"
            name="dialog_sign_pin_code"
          />
        </>
      )}

      <ActionsWrapper>
        {!props.isSend && (
          <ButtonWrapper>
            <CustomButton
              name="AlertButtonYes"
              color={"primary"}
              variant="contained"
              id={"AlertButtonYes"}
              disabled={props.pin?.length !== 14 || (props.isLegal && props.companyPin?.length !== 14)}
              onClick={props.onSendCode}
            >
              {t("label:digitalSign.sendCode")}
            </CustomButton>
          </ButtonWrapper>
        )}

        {props.isSend && (
          <ButtonWrapper>
            <CustomButton
              name="AlertButtonYes"
              color={"primary"}
              variant="contained"
              id={"AlertButtonYes"}
              disabled={props.code?.length !== 6}
              onClick={props.onSign}
            >
              {t("label:digitalSign.sign")}
            </CustomButton>
          </ButtonWrapper>
        )}

        <ButtonWrapper>
          <CustomButton
            color={"error"}
            variant="contained"
            name="AlertButtonNo"
            id={"AlertButtonNo"}
            onClick={props.onCancel}
          >
            {props.cancelLabel ?? t("label:digitalSign.cancel")}
          </CustomButton>
        </ButtonWrapper>
      </ActionsWrapper>
    </>
  );
};

export default EsignPinForm;

const MainText = styled.h1`
  color: var(--colorNeutralForeground1);
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 40px;
`;

const ButtonWrapper = styled.div``;

const Header = styled.div`
  font-size: 18px;
  font-weight: 500;
  line-height: 36px;
  text-align: left;
  color: var(--colorNeutralForeground1);
`;
